// src/components/Vendors/VendorsList.tsx
import React from 'react';
import { Edit2, Trash2, Mail, ChevronLeft, ChevronRight, Percent } from 'lucide-react';
import { useVendorFieldsStore } from '@store/vendorFieldsStore';
import type { VendorSearchResponse } from '@schemas/api';
import { formatVendorTaxIds } from '@/lib/vendorTaxIds';
import type { VendorPaymentProfile } from '@/types/payments';

interface VendorsListProps {
  vendors: VendorSearchResponse[];
  total: number;
  skip: number;
  limit: number;
  onEdit: (vendor: VendorSearchResponse) => void;
  onDelete: (vendor: VendorSearchResponse) => void;
  onPageChange: (skip: number) => void;
  onInvite?: (vendor: VendorSearchResponse) => void;
  onSetupPayments?: (vendor: VendorSearchResponse) => void;
  onPushToErp?: (vendor: VendorSearchResponse) => void;
  onManageInvoiceRules?: (vendor: VendorSearchResponse) => void;
  isLoading?: boolean;
  vendorPortalStatusByVendorId?: Record<string, string>;
  paymentProfilesByVendorId?: Record<string, VendorPaymentProfile>;
  emptyMessage?: string;
}

export const VendorsList: React.FC<VendorsListProps> = ({
  vendors,
  total,
  skip,
  limit,
  onEdit,
  onDelete,
  onPageChange,
  onInvite,
  onSetupPayments,
  onPushToErp,
  onManageInvoiceRules,
  isLoading = false,
  vendorPortalStatusByVendorId = {},
  paymentProfilesByVendorId = {},
  emptyMessage = 'No vendors found. Create one to get started.',
}) => {
  const { fields: configuredFields } = useVendorFieldsStore();

  // Use configured fields or fallback to default vendor columns if not configured
  const fields = configuredFields.length > 0 ? configuredFields : [
    { name: 'legal_name', label: 'Vendor Name', required: true },
    { name: 'status', label: 'Status', required: false },
    { name: 'default_payment_terms', label: 'Payment Terms', required: false },
    { name: 'tax_ids', label: 'Tax ID', required: false },
  ];

  const currentPage = Math.floor(skip / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const isVendorPortalConnected = (vendorId: string): boolean => {
    const portalStatus = vendorPortalStatusByVendorId[vendorId];
    return portalStatus === 'registered' || portalStatus === 'active';
  };

  if (vendors.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary dark:text-secondary-dark">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto border border-default dark:border-default-dark rounded-lg">
        <table className="w-full" role="grid" aria-label="Vendors table">
          <thead className="bg-raised dark:bg-raised-dark">
            <tr role="row">
              {fields.map((field) => (
                <th
                  key={field.name}
                  scope="col"
                  className="px-6 py-3 text-left text-sm font-semibold text-primary dark:text-primary-dark"
                  role="columnheader"
                >
                  {field.label}
                </th>
              ))}
              <th
                scope="col"
                className="px-6 py-3 text-left text-sm font-semibold text-primary dark:text-primary-dark"
                role="columnheader"
              >
                ERP Links
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-sm font-semibold text-primary dark:text-primary-dark"
                role="columnheader"
              >
                Payments
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-sm font-semibold text-primary dark:text-primary-dark"
                role="columnheader"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor, rowIndex) => (
              <tr
                key={vendor.id}
                className="border-t border-default dark:border-default-dark hover:bg-raised dark:hover:bg-raised-dark"
                role="row"
                aria-rowindex={skip + rowIndex + 2}
              >
                {fields.map((field) => {
                  const fieldName = field.name === 'tax_id' ? 'tax_ids' : field.name;
                  const value = vendor[fieldName as keyof VendorSearchResponse];
                  let displayValue: string = '—';

                  if (fieldName === 'tax_ids') {
                    displayValue = formatVendorTaxIds(vendor.tax_ids);
                  } else if (value !== null && value !== undefined) {
                    if (typeof value === 'string') {
                      displayValue = value;
                    } else if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else {
                      displayValue = String(value);
                    }
                  }

                  return (
                    <td
                      key={`${vendor.id}-${field.name}`}
                      className="px-6 py-3 text-sm text-primary dark:text-primary-dark"
                      role="cell"
                    >
                      {displayValue}
                    </td>
                  );
                })}
                <td className="px-6 py-3 text-sm text-primary dark:text-primary-dark" role="cell">
                  {vendor.erp_links && vendor.erp_links.length > 0 ? (
                    <div className="flex max-w-[260px] flex-wrap gap-1">
                      {vendor.erp_links
                        .filter((link) => link.status === 'active')
                        .slice(0, 3)
                        .map((link) => (
                          <span
                            key={link.id}
                            className="inline-flex max-w-[180px] truncate rounded border border-default px-2 py-0.5 text-xs text-content-secondary"
                            title={`${link.connection_label || `${link.erp_type} ${link.realm_id}`}: ${link.external_vendor_id}`}
                          >
                            {link.connection_label || link.erp_type}
                          </span>
                        ))}
                      {vendor.erp_links.filter((link) => link.status === 'active').length > 3 && (
                        <span className="text-xs text-content-muted">
                          +{vendor.erp_links.filter((link) => link.status === 'active').length - 3}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-secondary dark:text-secondary-dark">Not linked</span>
                  )}
                </td>
                <td className="px-6 py-3 text-sm text-primary dark:text-primary-dark" role="cell">
                  {(() => {
                    const profile = paymentProfilesByVendorId[vendor.id];
                    if (!profile) {
                      return <span className="text-secondary dark:text-secondary-dark">Not started</span>;
                    }
                    return (
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {profile.status.replace(/_/g, ' ')}
                      </span>
                    );
                  })()}
                </td>
                <td key={`${vendor.id}-actions`} className="px-6 py-3 text-sm" role="cell">
                  <div className="flex gap-2">
                    {(() => {
                      const portalStatus = vendorPortalStatusByVendorId[vendor.id];
                      const isPortalConnected = isVendorPortalConnected(vendor.id);
                      return (
                        <>
                          {onInvite && !isPortalConnected && (
                            <button
                              onClick={() => onInvite(vendor)}
                              disabled={isLoading}
                              aria-label={`Invite vendor ${vendor.legal_name}`}
                              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
                              title={portalStatus === 'pending_invitation' ? 'Resend vendor portal invite' : 'Invite vendor to portal'}
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {onPushToErp && !vendor.qb_vendor_id && (
                      <button
                        onClick={() => onPushToErp(vendor)}
                        disabled={isLoading}
                        aria-label={`Push vendor ${vendor.legal_name} to ERP`}
                        className="rounded border border-amber-200 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                        title="Push vendor to ERP"
                      >
                        Push to ERP
                      </button>
                    )}
                    {onSetupPayments && (
                      <button
                        onClick={() => onSetupPayments(vendor)}
                        disabled={isLoading}
                        aria-label={`Set up payments for ${vendor.legal_name}`}
                        className="rounded border border-blue-200 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        title="Set up payments"
                      >
                        Payments
                      </button>
                    )}
                    {onManageInvoiceRules && (
                      <button
                        onClick={() => onManageInvoiceRules(vendor)}
                        disabled={isLoading}
                        aria-label={`Manage invoice discount rules for ${vendor.legal_name}`}
                        className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                        title="Invoice discount rules"
                      >
                        <Percent className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onEdit(vendor)}
                      disabled={isLoading}
                      aria-label={`Edit vendor ${vendor.legal_name}`}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      title="Edit vendor"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(vendor)}
                      disabled={isLoading}
                      aria-label={`Delete vendor ${vendor.legal_name}`}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                      title="Delete vendor"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary dark:text-secondary-dark" aria-live="polite">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} vendors
        </p>
        <div className="flex gap-2" role="group" aria-label="Pagination">
          <button
            onClick={() => onPageChange(Math.max(0, skip - limit))}
            disabled={skip === 0 || isLoading}
            aria-label="Previous page"
            className="p-2 border border-default dark:border-default-dark rounded hover:bg-raised dark:hover:bg-raised-dark disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-3 py-2 text-sm text-primary dark:text-primary-dark" aria-current="page">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(skip + limit)}
            disabled={currentPage >= totalPages || isLoading}
            aria-label="Next page"
            className="p-2 border border-default dark:border-default-dark rounded hover:bg-raised dark:hover:bg-raised-dark disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
